import { Clock, CheckCircle2, XCircle, AlertCircle, MoreHorizontal, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn, formatRelativeTime, formatCronExpression } from '@/lib/utils';

interface PipelineCardProps {
  pipeline: {
    id: string;
    name: string;
    description?: string;
    lastExecution?: {
      status: 'success' | 'error' | 'running' | 'pending';
      startedAt: string;
      duration?: number;
    };
    nodeCount: number;
    healthStatus: 'healthy' | 'warning' | 'error';
    schedule?: string;
    nextRunAt?: string;
  };
  onRun: (id: string) => void;
  onEdit: (id: string) => void;
  onSchedule: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  // Bulk selection props
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  bulkMode?: boolean;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'running':
      return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
    case 'pending':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    default:
      return null;
  }
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '-';
  if (seconds < 60) return `~${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `~${minutes}min`;
}

export function PipelineCard({
  pipeline,
  onRun,
  onEdit,
  onSchedule,
  onDuplicate,
  onDelete,
  isSelected,
  onToggleSelect,
  bulkMode,
}: PipelineCardProps) {
  const { id, name, description, lastExecution, nodeCount, healthStatus, schedule, nextRunAt } = pipeline;

  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger selection if in bulk mode and not clicking on buttons/links
    if (bulkMode && onToggleSelect) {
      const target = e.target as HTMLElement;
      if (!target.closest('button') && !target.closest('a') && !target.closest('[role="menuitem"]')) {
        onToggleSelect(id);
      }
    }
  };

  return (
    <Card
      className={cn(
        "group hover:shadow-md transition-shadow",
        isSelected && "ring-2 ring-primary ring-offset-2",
        bulkMode && "cursor-pointer"
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        {/* Header: Checkbox + Name + More Menu */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {/* Bulk Selection Checkbox */}
            {bulkMode && onToggleSelect && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect(id)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-base truncate pr-2" title={name}>
                {name}
              </h3>
              {description && (
                <p className="text-sm text-muted-foreground truncate" title={description}>
                  {description}
                </p>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRun(id)}>
                <Clock className="h-4 w-4 mr-2" />
                Run Now
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(id)}>
                Edit Pipeline
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onSchedule(id)}>
                <CalendarClock className="h-4 w-4 mr-2" />
                {schedule ? 'Edit Schedule' : 'Configure Schedule'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDuplicate(id)}>
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(id)}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Execution Status */}
        <div className="flex items-center gap-2 mb-4">
          {lastExecution ? (
            <>
              {getStatusIcon(lastExecution.status)}
              <span className="text-sm capitalize text-muted-foreground">
                {lastExecution.status} • {formatRelativeTime(lastExecution.startedAt)}
              </span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">Never run</span>
          )}
        </div>

        {/* Schedule Info */}
        {schedule && (
          <div className="mb-4 p-2 bg-muted/50 rounded-md">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {formatCronExpression(schedule)}
                </p>
                {nextRunAt && (
                  <p className="text-xs text-muted-foreground">
                    Eksekusi berikutnya: {formatRelativeTime(nextRunAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4">
          <span>{nodeCount} node{nodeCount !== 1 ? 's' : ''}</span>
          {lastExecution?.duration && (
            <>
              <span>•</span>
              <span>{formatDuration(lastExecution.duration)} runtime</span>
            </>
          )}
        </div>

        {/* Health Indicator */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              healthStatus === 'healthy' && 'bg-green-500',
              healthStatus === 'warning' && 'bg-yellow-500',
              healthStatus === 'error' && 'bg-red-500'
            )}
          />
          <span className="text-xs text-muted-foreground capitalize">
            {healthStatus}
          </span>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="flex-1"
            onClick={(e) => { e.stopPropagation(); onRun(id); }}
          >
            Run
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={(e) => { e.stopPropagation(); onEdit(id); }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onDuplicate(id); }}
          >
            Duplicate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
